import { Box, useMediaQuery } from '@mui/material'
import { styled } from '@mui/material/styles'
import PropTypes from 'prop-types'
import { forwardRef, useCallback } from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'

// perfect-scrollbar applies `className` to its container imperatively (and
// overwrites whatever class the element already had), so an emotion class has
// to arrive through that prop. `styled` does exactly that: it resolves `sx`
// into a class and hands it over as `className`. Before this, `sx` was spread
// onto PerfectScrollbar, which neither understands nor forwards it, so every
// caller's sx (e.g. the notifications list's maxHeight) was silently dropped.
const StyledPerfectScrollbar = styled(PerfectScrollbar)({})

// Props only perfect-scrollbar understands; kept off the DOM on the native path.
const PERFECT_SCROLLBAR_ONLY_PROPS = [
    'option',
    'options',
    'onScrollY',
    'onScrollX',
    'onScrollUp',
    'onScrollDown',
    'onScrollLeft',
    'onScrollRight',
    'onYReachStart',
    'onYReachEnd',
    'onXReachStart',
    'onXReachEnd',
    'onSync',
    'component'
]

// Touch devices get native scrolling (momentum, rubber-banding, scroll
// chaining) instead of perfect-scrollbar's JS emulation. Detected by input
// capability, not user agent: iPadOS reports a desktop Mac UA, so the old UA
// sniff gave iPads the emulated scrollbar.
const NATIVE_SCROLL_QUERY = '(pointer: coarse)'

const Scrollbar = forwardRef((props, ref) => {
    const { children, containerRef, sx, className, style, ...other } = props
    const preferNative = useMediaQuery(NATIVE_SCROLL_QUERY, { noSsr: true })

    // Keep `containerRef` (perfect-scrollbar's hook for the scroll element)
    // working on the native path too, alongside a forwarded ref.
    const setNativeRef = useCallback(
        (node) => {
            if (typeof ref === 'function') {
                ref(node)
            } else if (ref) {
                ref.current = node
            }
            containerRef?.(node)
        },
        [ref, containerRef]
    )

    if (preferNative) {
        const domProps = { ...other }
        for (const key of PERFECT_SCROLLBAR_ONLY_PROPS) {
            delete domProps[key]
        }
        return (
            <Box
                ref={setNativeRef}
                className={className}
                style={style}
                sx={[
                    { overflow: 'auto', WebkitOverflowScrolling: 'touch' },
                    ...(Array.isArray(sx) ? sx : [sx])
                ]}
                {...domProps}
            >
                {children}
            </Box>
        )
    }

    return (
        <StyledPerfectScrollbar
            ref={ref}
            containerRef={containerRef}
            className={className}
            style={style}
            sx={sx}
            {...other}
        >
            {children}
        </StyledPerfectScrollbar>
    )
})

Scrollbar.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    containerRef: PropTypes.func,
    style: PropTypes.object,
    sx: PropTypes.oneOfType([
        PropTypes.arrayOf(
            PropTypes.oneOfType([
                PropTypes.func,
                PropTypes.object,
                PropTypes.bool
            ])
        ),
        PropTypes.func,
        PropTypes.object
    ])
}

export default Scrollbar
